<script setup lang="ts">
/**
 * Aura AGM (multi) — Notice for Controller (Change in Particulars, CORPORATE) · CA 1967 s.386AH(1).
 * 개인(NoticeControllerDoc)과 동일 틀이나 법인 컨트롤러 particulars(UEN·관할·법적형태·대표자 등).
 * Yes/No 조건부: is_still_controller, has_change (플래그를 fill 에 기록). cdd 는 형제 agm 모듈 재사용.
 */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from '../../agm/cdd/CddVarField.vue';
import AgmSigners from '../../agm/cdd/AgmSigners.vue';
import { useAgmFill, DOC_SCOPE } from '../../agm/cdd/useCddFill';
import '../../agm/docs/agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const FLAGS = ['is_still_controller', 'has_change'] as const;
const DEF: Record<string, boolean> = { is_still_controller: true, has_change: false };
const flags = ref<Record<string, boolean>>({ ...DEF });

function read(k: string): boolean {
  const v = String(fill.values[K(k)] ?? '').trim().toLowerCase();
  if (!v) return DEF[k];
  return v === 'yes' || v === 'true' || v === '1';
}
function sync() { for (const k of FLAGS) flags.value[k] = read(k); }
function toggle(k: string) { flags.value[k] = !flags.value[k]; fill.setValue(K(k), flags.value[k] ? 'yes' : 'no'); }
onMounted(() => {
  for (const k of FLAGS) if (!String(fill.values[K(k)] ?? '').trim()) fill.setValue(K(k), DEF[k] ? 'yes' : 'no');
  sync();
});
watch(() => FLAGS.map((k) => fill.values[K(k)]).join('|'), () => sync());

const PARTICULARS = [
  ['corporate_controller_name', 'Name of corporate controller'],
  ['controller_entity_name', 'Registered name of the entity'],
  ['controller_entity_uen', 'Unique Entity Number (UEN) / ID'],
  ['controller_legal_form', 'Legal form / status'],
  ['controller_jurisdiction', 'Jurisdiction of incorporation'],
  ['controller_corporate_register_name', 'Name of corporate register'],
  ['controller_registered_office_address', 'Registered office address'],
  ['controller_representative_name', 'Name of representative'],
  ['controller_representative_identification_no', "Representative's identification no."],
  ['controller_entity_email', 'Email address'],
  ['controller_entity_contact_no', 'Contact no.'],
  ['controller_date_of_becoming', 'Date of becoming a controller'],
  ['controller_date_of_cessation', 'Date of cessation as a controller'],
] as const;
</script>

<template>
  <div class="agm-doc">
    <div class="doc-title">FORM OF NOTICE MENTIONED IN SECTION 386AH(1) OF COMPANIES ACT — NOTICE FOR CHANGE IN PARTICULARS</div>
    <p class="doc-p">Date of notice: <CddVarField field-key="notice_date" label="Notice Date" /></p>
    <p class="doc-p">Dear <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" />,</p>
    <p class="doc-p">
      We know or have reasonable grounds to believe that a relevant change has occurred in your particulars that are stated in the
      register of controllers of <CddVarField field-key="company_name" label="Company Name" />. This notice is sent under section
      386AH(1) of the Companies Act 1967 and requires you to provide the following information within 30 days.
    </p>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">1. Are you still a registrable controller of <CddVarField field-key="company_name" label="Company Name" />?</td>
          <td>
            <span class="cb-row">
              <span class="doc-toggle" :class="{ off: !flags.is_still_controller }" @click="toggle('is_still_controller')">
                {{ flags.is_still_controller ? '☒ Yes' : '☐ Yes' }} / {{ flags.is_still_controller ? '☐ No' : '☒ No' }}
              </span>
            </span>
            <div v-if="!flags.is_still_controller" style="margin-top:6px">
              Date ceased: <CddVarField field-key="date_ceased_as_controller" label="Date Ceased" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p">2. Your particulars stated in the register of controllers are set out below.</p>
    <table class="cdd-tbl">
      <tbody>
        <tr v-for="p in PARTICULARS" :key="p[0]">
          <td class="cdd-lcell">{{ p[1] }}</td>
          <td><CddVarField :field-key="p[0]" :label="p[1]" /></td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Has a relevant change in your particulars occurred?</td>
          <td>
            <span class="doc-toggle" :class="{ off: !flags.has_change }" @click="toggle('has_change')">
              {{ flags.has_change ? '☒ Yes' : '☐ Yes' }} / {{ flags.has_change ? '☐ No' : '☒ No' }}
            </span>
            <div v-if="flags.has_change" style="margin-top:6px">
              (a) Date of the change: <CddVarField field-key="date_of_change" label="Date of Change" /><br />
              Particulars of the change: <CddVarField field-key="particulars_of_change" label="Particulars of Change" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:14px">Yours sincerely,</p>
    <!-- 발송자 = 해당 엔티티 비서(자동채움). docx 는 [[secretary_name]] — 컨트롤러 [[Signers]] 와 별개 변수. -->
    <div class="sign-block"><div class="sign-line"><CddVarField field-key="secretary_name" label="Company Secretary" /><br />Company Secretary</div></div>

    <div class="sec-title" style="margin-top:16px">ACKNOWLEDGEMENT</div>
    <p class="doc-p">
      We, <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" /> (UEN:
      <CddVarField field-key="controller_entity_uen" label="UEN" />), hereby declare that the above information provided is true and correct.
    </p>
    <p class="doc-p">Signature (Authorised Representative):</p>
    <AgmSigners var-key="notice_Signers" board-label="Corporate Controller" />
    <p class="doc-p">Date: <CddVarField field-key="dated_on" label="Dated On" /></p>
  </div>
</template>
